/**
 * Where the league's data actually is, and how to get a copy of it.
 *
 * This screen used to have one job — apologise for localStorage — because there was only one
 * store. There are two now, and the honest version of the screen is different in each:
 *
 *   ON THE LEAGUE DATABASE it reports that work is shared, and offers backup and restore anyway.
 *   A hosted database is not a backup: it protects against a lost laptop and not at all against
 *   somebody deleting the wrong row, which on a ten-man league with one admin is the likelier
 *   accident.
 *
 *   IN THIS BROWSER — the offline preview, and the fallback if Firebase cannot be reached — it
 *   reports whether saving is actually working. Not "we support localStorage": whether a write to
 *   this browser, in this window, succeeded. Sandboxes and private windows block it, and the
 *   screen has to name that rather than show a reassuring tick.
 *
 * RESTORE MEANS TWO DIFFERENT THINGS and the difference matters enough to be spelled out on the
 * button. Into your own browser it is a REPLACE: a backup is a picture of a moment and merging two
 * pictures produces a third that never existed. Into the shared league it is an UPSERT: rows in the
 * file overwrite their counterparts, rows not in the file are left alone. Replacing there would
 * delete nine other people's work on the strength of one person's stale download.
 */

import { useEffect, useRef, useState } from 'react';
import { dock } from './dock.js';
import { atLeast, type LeagueStore, type Role } from '../store/types.js';
import type { Snapshot } from '../store/memoryStore.js';
import { asFirestore, asMemoryStore } from '../store/provider.js';

const SEASON = 2026;

export function SavedData({ role, store }: { role: Role; store: LeagueStore }) {
  const mem = asMemoryStore(store);
  const fs = asFirestore(store);
  const canEdit = atLeast(role, 'commissioner');
  const [, bump] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<Snapshot | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Repaint whenever anything is written, so "last saved" is not a lie by the time it is read.
  useEffect(() => store.subscribe(SEASON, () => setTimeout(() => bump((n) => n + 1), 500)), [store]);

  // A tab being closed is the one moment a debounce can lose something. Firestore has no debounce
  // to flush — the SDK owns its own queue — so this is the memory store's problem alone.
  useEffect(() => {
    if (!mem) return undefined;
    const flush = () => mem.flush();
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', flush);
    };
  }, [mem]);

  const counts = mem?.counts() ?? fs?.counts() ?? [];
  const total = counts.reduce((n, c) => n + c.n, 0);
  const snapshotOf = () => (mem ? mem.snapshot() : fs!.snapshot());

  function download() {
    const snap = snapshotOf();
    const stamp = snap.savedAt.slice(0, 10);
    const blob = new Blob([JSON.stringify(snap, null, 1)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ffl-league-data-${stamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setFlash('Backup downloaded.');
    setTimeout(() => setFlash(null), 5000);
  }

  async function chooseFile(file: File) {
    setProblem(null);
    try {
      const snap = JSON.parse(await file.text()) as Snapshot;
      if (snap.version !== 1 || !Array.isArray(snap.teamPairings)) {
        throw new Error('That does not look like a backup from this app.');
      }
      setConfirming(snap);
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'That file could not be read.');
    }
  }

  async function restore() {
    if (!confirming) return;
    setBusy(true);
    setProblem(null);
    try {
      if (mem) {
        mem.load(confirming);
        mem.flush();
        setFlash('Restored. Every screen has been repainted from the backup.');
      } else {
        await fs!.restore(confirming);
        setFlash('Restored into the league database. Everyone sees it.');
      }
      setConfirming(null);
      setTimeout(() => setFlash(null), 6000);
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'The restore did not finish.');
    } finally {
      setBusy(false);
    }
  }

  /* Arm-then-act, like every other irreversible control in the house. */
  const [armed, setArmed] = useState(false);
  function clearAll() {
    if (!mem) return;
    mem.clearSaved();
    mem.load({
      version: 1,
      league: '',
      savedAt: new Date().toISOString(),
      settings: [],
      declarations: [],
      preferences: [],
      trades: [],
      faab: [],
      farmNominations: [],
      draftStates: [],
      picks: [],
      notes: [],
      interpretations: [],
      overrides: [],
      mockKeepers: [],
      proposals: [],
      dockets: [],
      teamPairings: [],
      managers: [],
    });
    setArmed(false);
    setFlash('Cleared. Only what shipped with the app is left.');
    setTimeout(() => setFlash(null), 6000);
  }

  /** The store's own tally, applied to any snapshot: the file's or the app's. */
  const countsOf = (snap: Snapshot): Counts => [
    { what: 'manager pairings', n: snap.teamPairings?.length ?? 0 },
    { what: 'managers amended', n: snap.managers?.length ?? 0 },
    { what: 'trades', n: snap.trades?.length ?? 0 },
    { what: 'keeper declarations', n: snap.declarations?.length ?? 0 },
    { what: 'slot preferences', n: snap.preferences?.length ?? 0 },
    { what: 'FAAB entries', n: snap.faab?.length ?? 0 },
    { what: 'picks made', n: snap.picks?.length ?? 0 },
    { what: 'farm nominations', n: snap.farmNominations?.length ?? 0 },
    { what: 'rule proposals', n: snap.proposals?.length ?? 0 },
    { what: 'commissioner notes', n: snap.notes?.length ?? 0 },
    { what: 'interpretations', n: snap.interpretations?.length ?? 0 },
    { what: 'overrides', n: snap.overrides?.length ?? 0 },
  ];
  /* A phone is a bad place to overwrite a league (rev B, screen 17). Same breakpoint as every sheet. */
  const phone = typeof window !== 'undefined' && window.matchMedia('(max-width: 899px)').matches;

  return (
    <div className="app admin saved">
      {fs && <LiveState fs={fs} />}
      {mem && <BrowserState mem={mem} total={total} />}

      {counts.some((c) => c.n > 0) && (
        <section className="card">
          <div className="cardhead"><h3>What is held</h3></div>
          <ul className="tallygrid">
            {counts.filter((c) => c.n > 0).map((c) => (
              <li key={c.what}><b>{c.n.toLocaleString()}</b><span>{c.what}</span></li>
            ))}
          </ul>
        </section>
      )}

      {flash && <p className="fact said">{flash}</p>}
      {problem && <p className="estaxbad">{problem}</p>}

      <div className="twoup datacards">
        <section className="card">
          <div className="cardhead"><h3>Download a backup</h3></div>
          <p className="fact pad">
            {fs
              ? 'A hosted database does not protect against deleting the wrong row. Take one before anything irreversible.'
              : 'One JSON file holding everything entered here. It moves work between devices, and it is how the pre-2009 pairings get built in for good.'}
          </p>
          <div className="saveacts">
            <button type="button" className="btn" onClick={download} disabled={total === 0}>
              Download a backup
            </button>
          </div>
        </section>

        {canEdit && (
          <section className="card">
            <div className="cardhead">
              <h3>Restore from a file</h3>
              <span className="pill grey">Commissioner</span>
            </div>
            <p className="fact pad">
              {fs
                ? 'Rows in the file overwrite theirs; rows not in the file are left alone.'
                : 'Replaces what is in this browser. A backup is a moment, and two moments do not merge.'}
            </p>
            <div className="saveacts">
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void chooseFile(f);
                  e.target.value = '';
                }}
              />
              <button type="button" className="btn" disabled={phone} onClick={() => fileRef.current?.click()}>
                {phone ? 'Restore from a file' : 'Choose a file'}
              </button>
            </div>
            {phone && (
              <p className="cardfoot fact">Restores run from a desktop — a phone is a bad place to overwrite a league.</p>
            )}
          </section>
        )}
      </div>

      {canEdit && mem && mem.persistence.available && total > 0 && (
        <section className="card">
          <div className="cardhead"><h3>Start over</h3></div>
          <p className="fact pad">
            Clears this browser. Archives, rulebook and recorded manager facts ship with the app — untouched.
          </p>
          <div className="saveacts">
            {armed ? (
              <span className="armed">
                <button type="button" className="btn danger" onClick={clearAll}>Clear it</button>
                <button type="button" className="btn" onClick={() => setArmed(false)}>Keep it</button>
              </span>
            ) : (
              <button type="button" className="btn quietred" onClick={() => setArmed(true)}>
                Clear everything saved here
              </button>
            )}
          </div>
        </section>
      )}

      {confirming && (
        <RestoreConfirm
          league={Boolean(fs)}
          file={countsOf(confirming)}
          now={countsOf(snapshotOf())}
          savedAt={confirming.savedAt}
          busy={busy}
          onCancel={() => setConfirming(null)}
          onGo={() => void restore()} />
      )}
    </div>
  );
}

/**
 * The restore confirm: a count table, FILE against NOW, per kind (rev B, screen 10). The sentence
 * under it says what a restore means in this store, and the button is red because it writes over
 * the league.
 */
function RestoreConfirm(
  { league, file, now, savedAt, busy, onCancel, onGo }: {
    league: boolean; file: Counts; now: Counts; savedAt: string; busy: boolean;
    onCancel: () => void; onGo: () => void;
  },
) {
  const rows = file.map((f, i) => ({ what: f.what, file: f.n, now: now[i]?.n ?? 0 }))
    .filter((r) => r.file > 0 || r.now > 0);
  const when = new Date(savedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return dock(
    <>
      <button type="button" className="sheetscrim kdscrim" aria-label="Cancel" onClick={onCancel} />
      <div className="kdsheet restoredlg" role="dialog" aria-modal="true" aria-label="Restore">
        <h2 className="kdsheeth">{league ? 'Write this file into the league?' : 'Replace everything with this file?'}</h2>
        <table className="restoretab">
          <thead>
            <tr><th /><th className="num">File · {when}</th><th className="num">Now</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.what}>
                <th scope="row">{r.what.charAt(0).toUpperCase() + r.what.slice(1)}</th>
                <td className="num"><b>{r.file.toLocaleString()}</b></td>
                <td className="num mut">{r.now.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="fact">
          {league
            ? 'Rows in the file overwrite their counterparts. Rows not in the file are left alone.'
            : 'Restoring replaces what is in this browser. A backup is a moment; merging two produces a third that never existed.'}
        </p>
        <div className="restoreacts">
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn danger" disabled={busy} onClick={onGo}>
            {busy ? 'Writing…' : league ? 'Write into the league' : 'Replace and restore'}
          </button>
        </div>
      </div>
    </>,
  );
}

/* -- the two status panels ------------------------------------------------------------------- */

type Counts = Array<{ what: string; n: number }>;

function LiveState({ fs }: { fs: NonNullable<ReturnType<typeof asFirestore>> }) {
  const { ready, error, savedAt } = fs.status;
  const ok = ready && !error;
  const last = savedAt ? new Date(savedAt) : null;
  const when = last
    ? `${last.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}${last.toDateString() === new Date().toDateString() ? ' today' : ` ${last.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}`
    : null;
  return (
    <section className={`card datahead ${ok ? '' : error ? 'bad' : ''}`}>
      <div className="dataheadrow">
        <div>
          <h3>{ok ? 'Shared with the league' : error ? 'The league database refused a read' : 'Connecting…'}</h3>
          <p className="fact">
            {error
              ? `${error} This is almost always a permission rule rather than a fault: an account signed in but not yet paired to a manager can read nothing. If that is not it, tell the admin what this line says.`
              : `Work reaches every other manager as it is made.${when ? ` Last write ${when}.` : ''}`}
          </p>
        </div>
        <span className={`pill ${ok ? 'green' : error ? 'amber' : 'grey'}`}>League database</span>
      </div>
    </section>
  );
}

function BrowserState(
  { mem, total }: { mem: NonNullable<ReturnType<typeof asMemoryStore>>; total: number },
) {
  const { available, reason, savedAt, bytes } = mem.persistence;
  const when = savedAt ? new Date(savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
  return (
    <>
      <section className="card datahead">
        <div className="dataheadrow">
          <div>
            <h3>{available ? 'Saved in this browser' : 'Not being saved'}</h3>
            <p className="fact">
              {available
                ? `${when ? `Last saved ${when}` : 'Nothing saved yet'}${bytes > 0 ? ` · ${(bytes / 1024).toFixed(0)} KB` : ''}. ${total > 0
                  ? 'Does not follow you to your phone; a backup file moves work between devices.'
                  : 'As soon as you pair a manager or record a trade it is kept here.'}`
                : `${reason ?? 'This window refused the write — private windows do.'} Everything entered is lost on reload. Download a backup before closing.`}
            </p>
          </div>
          <span className={`pill ${available ? 'grey' : 'amber'}`}>{available ? 'This browser only' : 'Write failed'}</span>
        </div>
      </section>
    </>
  );
}
